import { Box, Grid, Paper, Typography, Chip } from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { industryMeta } from '../ListGroups/SuccessCaseMeta';
import { successCaseTypography, successCaseCard } from '../designTokens';

const ClientCard = ({ caseItem }) => {
    const clientName = caseItem.alt;
    const products = caseItem.products || [];

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: successCaseCard.minHeight,
                p: 2,
                borderRadius: '8px',
                border: '1px solid #E9EBED',
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: successCaseCard.logoHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: '#F3F4F6',
                    flexShrink: 0,
                }}
            >
                <Box
                    component="img"
                    src={caseItem.src}
                    alt={clientName || products[0]?.text || '合作客戶'}
                    loading="lazy"
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                    }}
                />
            </Box>

            {clientName && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        mb: 1,
                    }}
                >
                    <BusinessOutlinedIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    <Typography sx={successCaseTypography.cardSecondary}>{clientName}</Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                {products.map((product, idx) => (
                    <Typography key={idx} sx={{ ...successCaseTypography.cardTitle, textAlign: 'center' }}>
                        {product.text}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

const IndustrySection = ({ industryKind, caseList, index, reverse }) => {
    const meta = industryMeta[industryKind] || { id: industryKind, tag: industryKind, summary: '' };
    const imageFirst = !reverse;
    const previewCases = caseList.slice(0, 4);

    const imageBlock = (
        <Box
            sx={{
                width: '100%',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #E9EBED',
                background: 'linear-gradient(145deg, #F5FCFC 0%, #E8EDF2 100%)',
                p: { xs: 2, md: 3 },
                minHeight: { xs: 220, md: 280 },
            }}
        >
            <Typography sx={{ ...successCaseTypography.blockLabel, mb: 2 }}>代表客戶</Typography>
            <Grid container spacing={1.5}>
                {previewCases.map((item, i) => (
                    <Grid item xs={6} key={i}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 100,
                                p: 1.5,
                                borderRadius: '8px',
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E9EBED',
                            }}
                        >
                            <Box
                                component="img"
                                src={item.src}
                                alt={item.alt || ''}
                                sx={{ maxWidth: '100%', maxHeight: 72, objectFit: 'contain' }}
                            />
                        </Box>
                    </Grid>
                ))}
            </Grid>
            {caseList.length > 4 && (
                <Typography sx={{ ...successCaseTypography.body, mt: 2, textAlign: 'center', fontSize: '15px' }}>
                    及其他 {caseList.length - 4} 家合作客戶
                </Typography>
            )}
        </Box>
    );

    const introBlock = (
        <Box>
            <Chip
                label={meta.tag}
                size="small"
                sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    fontSize: '14px',
                    backgroundColor: '#E8EDF2',
                    color: '#4A6984',
                    border: '1px solid rgba(74, 105, 132, 0.25)',
                }}
            />
            <Typography component="h3" sx={{ ...successCaseTypography.sectionTitle, mb: 0.5 }}>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: '#03363D',
                        color: '#fff',
                        fontSize: '1rem',
                        mr: 1.5,
                        verticalAlign: 'middle',
                    }}
                >
                    {index + 1}
                </Box>
                {industryKind}
            </Typography>
            <Typography sx={{ ...successCaseTypography.sectionCount, mb: 2.5, pl: { md: 5.5 } }}>
                共 {caseList.length} 項合作案例
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 0,
                    backgroundColor: '#F8F9F9',
                    borderLeft: '4px solid #4A6984',
                    borderRadius: '4px',
                }}
            >
                <Typography sx={{ ...successCaseTypography.blockLabel, mb: 1 }}>產業說明</Typography>
                <Typography sx={successCaseTypography.body}>{meta.summary}</Typography>
            </Paper>
        </Box>
    );

    const cardsBlock = (
        <Box sx={{ mt: 3 }}>
            <Typography sx={{ ...successCaseTypography.blockLabel, mb: 2 }}>合作案例</Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: `${successCaseCard.gap}px`,
                }}
            >
                {caseList.map((caseItem, caseIndex) => (
                    <Box
                        key={caseIndex}
                        sx={{
                            width: {
                                xs: '100%',
                                sm: 'calc(50% - 12px)',
                                md: 'calc(33.333% - 16px)',
                                lg: `${successCaseCard.width}px`,
                            },
                            flexShrink: 0,
                        }}
                    >
                        <ClientCard caseItem={caseItem} />
                    </Box>
                ))}
            </Box>
        </Box>
    );

    return (
        <Box
            component="section"
            id={meta.id}
            sx={{
                mb: { xs: 5, md: 7 },
                scrollMarginTop: '80px',
            }}
        >
            <Grid container spacing={{ xs: 3, md: 5 }} alignItems="flex-start">
                {imageFirst ? (
                    <>
                        <Grid item xs={12} md={5}>
                            {imageBlock}
                        </Grid>
                        <Grid item xs={12} md={7}>
                            {introBlock}
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
                            {introBlock}
                        </Grid>
                        <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
                            {imageBlock}
                        </Grid>
                    </>
                )}
            </Grid>
            {cardsBlock}
        </Box>
    );
};

export default IndustrySection;
